/**
 * The scan's own sky, and keeping clouds out of the scan.
 *
 * Outdoor scans almost always bake the sky into a shell of splats close to the capture point
 * (often 10 to 30 units away). Clouds at a believable height sit behind that shell and are hidden
 * by it, correctly, because unified splats are sorted by depth. Two tools come from one density
 * grid of the scan, built once from its splat centres:
 *
 * 1. Clearance. Cloud splats that drift into dense scan content (a mountain, a sign, a building)
 *    dissolve, so clouds never intersect scanned objects. Sparse cells (a baked sky shell, far
 *    background) don't count: real geometry holds hundreds of splats per cell, a sky shell a few.
 *
 * 2. Replace the scan's sky. A classifier in the scan's copy stage (its part of the work-buffer
 *    modifier, see workBufferModifiers.ts, so it costs nothing per frame) fades splats that are
 *    sky: above the horizon seen from the capture
 *    point, beyond a keep radius (the objects around the capture point), in sparse cells, and
 *    sky-coloured (not near-black, not foliage green). Clouds can then sit at any height in front of the
 *    scene's skybox or a procedural sky backdrop (proceduralSky.ts). "Keep" (the default) leaves
 *    the scan alone; new cloud layers are then fitted inside its sky shell.
 *
 * Pushing the sky splats "behind" the clouds instead (re-depth-sorting them) is not an option:
 * on WebGL2 the CPU sorter orders splats by the resource's stored centres, and rewriting those for
 * a scan would also move what picking, bounds and collision read.
 */
import * as pc from 'playcanvas';

import type { ScanSkySettings } from '../../types';
import { setWorkBufferModifierPart } from '../workBufferModifiers';

export type { ScanSkySettings };
export type ScanSkyMode = 'keep' | 'replace';
export type SkyBackdrop = 'clear' | 'hazy' | 'golden' | 'overcast' | 'none';

export interface ResolvedScanSky {
    mode: ScanSkyMode;
    strength: number;
    horizon: number;
    keepRadius: number | null;
    center: { x: number; y: number; z: number } | null;
    backdrop: SkyBackdrop;
}

export function resolveScanSky(s: ScanSkySettings | null | undefined): ResolvedScanSky {
    const num = (v: unknown, d: number, lo: number, hi: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d);
    const backdrops: SkyBackdrop[] = ['clear', 'hazy', 'golden', 'overcast', 'none'];
    return {
        mode: s?.mode === 'replace' ? 'replace' : 'keep',
        strength: num(s?.strength, 1, 0, 1),
        horizon: num(s?.horizon, 4, -10, 60),
        keepRadius: typeof s?.keepRadius === 'number' && Number.isFinite(s.keepRadius) && s.keepRadius > 0 ? s.keepRadius : null,
        center: s?.center && [s.center.x, s.center.y, s.center.z].every(Number.isFinite) ? { ...s.center } : null,
        backdrop: backdrops.includes(s?.backdrop as SkyBackdrop) ? (s!.backdrop as SkyBackdrop) : 'clear',
    };
}

// ── density grid (pure) ──────────────────────────────────────────────────────────────────

export interface ScanDensityGrid {
    /** World position of the grid's min corner. */
    origin: [number, number, number];
    /** World size of a cell. */
    cell: number;
    dims: [number, number, number];
    /** Splats per cell (x fastest, then z, then y). */
    counts: Float32Array;
    /** Cells at or above this count are solid scan content. */
    threshold: number;
}

/**
 * Count splat centres (world space, xyz triples) into a grid over `bounds`. Cell size: the
 * bounds' largest extent over `resolution` (so the grid has up to resolution cells on that axis).
 * The threshold is a fraction of the "typical dense" count: the count at which half of all splats
 * live in cells at least that full, so it adapts to the scan's density and units.
 */
export function buildScanDensityGrid(
    centers: ArrayLike<number>,
    bounds: { min: [number, number, number]; max: [number, number, number] },
    resolution = 112,
    stride = 1,
    /** Splats each sample stands for (LOD scans sample their octree leaves); default 1. */
    weights?: ArrayLike<number>,
): ScanDensityGrid | null {
    const ext = [0, 1, 2].map((i) => bounds.max[i] - bounds.min[i]);
    const largest = Math.max(...ext);
    if (!(largest > 0) || !Number.isFinite(largest)) return null;
    const cell = largest / resolution;
    const dims = ext.map((e) => Math.max(1, Math.min(160, Math.ceil(e / cell)))) as [number, number, number];
    const [gx, gy, gz] = dims;
    const counts = new Float32Array(gx * gy * gz);
    const o = bounds.min;
    const n = Math.floor(centers.length / 3);
    for (let i = 0; i < n; i += stride) {
        const x = Math.floor((centers[i * 3] - o[0]) / cell);
        const y = Math.floor((centers[i * 3 + 1] - o[1]) / cell);
        const z = Math.floor((centers[i * 3 + 2] - o[2]) / cell);
        if (x < 0 || y < 0 || z < 0 || x >= gx || y >= gy || z >= gz) continue;
        counts[x + gx * (z + gz * y)] += weights ? weights[i] : 1;
    }
    // dense-median: sort non-empty cells by count; walk from the top until half the splats
    const filled: number[] = [];
    let total = 0;
    for (let i = 0; i < counts.length; i++) {
        if (counts[i] > 0) {
            filled.push(counts[i]);
            total += counts[i];
        }
    }
    if (!filled.length) return null;
    filled.sort((a, b) => b - a);
    let acc = 0;
    let denseMedian = filled[0];
    for (const c of filled) {
        acc += c;
        denseMedian = c;
        if (acc >= total / 2) break;
    }
    const threshold = Math.max(3, denseMedian * 0.06);
    return { origin: [o[0], o[1], o[2]], cell, dims, counts, threshold };
}

/**
 * The grid as RG8 layers for the GPU (one x-z slice per y): R = count relative to the threshold
 * (64 = threshold), G = the same, dilated by one cell (max of the 3x3x3 neighbourhood) for cloud
 * clearance, so a cloud splat centred just outside an object still clears it.
 */
export function densityLayers(grid: ScanDensityGrid): Uint8Array[] {
    const [gx, gy, gz] = grid.dims;
    const rel = (x: number, y: number, z: number) => Math.min(255, Math.round((grid.counts[x + gx * (z + gz * y)] / grid.threshold) * 64));
    const layers: Uint8Array[] = [];
    for (let y = 0; y < gy; y++) {
        const layer = new Uint8Array(gx * gz * 2);
        for (let z = 0; z < gz; z++) {
            for (let x = 0; x < gx; x++) {
                let dil = 0;
                for (let dy = -1; dy <= 1; dy++) {
                    const yy = y + dy;
                    if (yy < 0 || yy >= gy) continue;
                    for (let dz = -1; dz <= 1; dz++) {
                        const zz = z + dz;
                        if (zz < 0 || zz >= gz) continue;
                        for (let dx = -1; dx <= 1; dx++) {
                            const xx = x + dx;
                            if (xx < 0 || xx >= gx) continue;
                            const v = rel(xx, yy, zz);
                            if (v > dil) dil = v;
                        }
                    }
                }
                const o = (x + gx * z) * 2;
                layer[o] = rel(x, y, z);
                layer[o + 1] = dil;
            }
        }
        layers.push(layer);
    }
    return layers;
}

export interface SkyShellEstimate {
    /** Distance around the capture point holding the scan's objects above the horizon. */
    keepRadius: number;
    /**
     * Median distance of the sparse cells above the horizon when they form a baked sky shell (they
     * cover most of the sky, overhead included: a mountain range or a building only covers a band
     * or a patch of it), else null.
     */
    shellDistance: number | null;
    /** Share (0-1) of the sky between 20 and 75 degrees up that sparse cells cover. */
    skyCoverage: number;
    /** Height (world y) of the top of the dense content near the capture point. */
    denseTop: number;
    /** Height (world y) of the ground at the capture point (lowest dense content nearby). */
    ground: number;
}

/** Measure where the scan's objects end and its baked sky begins, seen from `center` (world). */
export function estimateSkyShell(grid: ScanDensityGrid, center: [number, number, number], horizonDeg: number): SkyShellEstimate {
    const [gx, gy, gz] = grid.dims;
    const sinH = Math.sin((horizonDeg * Math.PI) / 180);
    const denseDist: number[] = [];
    const sparseDist: number[] = [];
    // directions of the sparse cells: 24 headings x 6 heights between 20 and 75 degrees up
    const AZ = 24, EL = 6;
    const covered = new Uint8Array(AZ * EL);
    const sin20 = Math.sin((20 * Math.PI) / 180);
    const sin75 = Math.sin((75 * Math.PI) / 180);
    let denseTop = -Infinity;
    let ground = Infinity;
    for (let y = 0; y < gy; y++) {
        for (let z = 0; z < gz; z++) {
            for (let x = 0; x < gx; x++) {
                const c = grid.counts[x + gx * (z + gz * y)];
                if (!c) continue;
                const px = grid.origin[0] + (x + 0.5) * grid.cell;
                const py = grid.origin[1] + (y + 0.5) * grid.cell;
                const pz = grid.origin[2] + (z + 0.5) * grid.cell;
                const dx = px - center[0], dy = py - center[1], dz = pz - center[2];
                const r = Math.hypot(dx, dy, dz);
                const horiz = Math.hypot(dx, dz);
                const dense = c >= grid.threshold;
                if (dense && horiz < Math.max(grid.cell * 6, 1e-6) * 4) {
                    ground = Math.min(ground, py);
                }
                if (r < 1e-6 || dy / r < sinH) continue;
                if (dense) denseDist.push(r);
                else if (dy / r > Math.sin((Math.max(horizonDeg, 0) + 8) * (Math.PI / 180))) sparseDist.push(r);
                if (dense) denseTop = Math.max(denseTop, py);
                const se = dy / r;
                if (!dense && se >= sin20 && se <= sin75) {
                    const az = Math.min(AZ - 1, Math.floor(((Math.atan2(dz, dx) + Math.PI) / (2 * Math.PI)) * AZ));
                    const el = Math.min(EL - 1, Math.floor(((Math.asin(se) - Math.asin(sin20)) / (Math.asin(sin75) - Math.asin(sin20))) * EL));
                    covered[az + AZ * el] = 1;
                }
            }
        }
    }
    denseDist.sort((a, b) => a - b);
    sparseDist.sort((a, b) => a - b);
    const q = (a: number[], p: number) => (a.length ? a[Math.min(a.length - 1, Math.floor(a.length * p))] : NaN);
    const keep = denseDist.length ? q(denseDist, 0.9) * 1.15 : grid.cell * 4;
    let coveredBins = 0;
    for (let i = 0; i < covered.length; i++) coveredBins += covered[i];
    const skyCoverage = coveredBins / covered.length;
    const shell = sparseDist.length >= 20 && skyCoverage >= 0.5 ? q(sparseDist, 0.5) : NaN;
    // with a baked sky, objects that reach into it don't keep the whole shell around them
    const keepCapped = Number.isFinite(shell) ? Math.min(keep, shell * 0.75) : keep;
    return {
        keepRadius: Math.max(grid.cell * 2, keepCapped),
        shellDistance: Number.isFinite(shell) ? shell : null,
        skyCoverage,
        denseTop: Number.isFinite(denseTop) ? denseTop : center[1],
        ground: Number.isFinite(ground) ? ground : center[1] - 1.7,
    };
}

// ── the scan's copy stage: sky classifier ────────────────────────────────────────────────
// uSkyCenter    xyz capture point (world), w 1 = classifier on
// uSkyParams    x sin(horizon), y keep radius, z strength 0..1, w 1 = tint instead of hide (editor preview)
// uSkyGrid      xyz grid origin (world), w cell size
// uSkyGridSize  xyz cells, w 1 = grid present
// uSkyDensity   RG8 2D array (y layers): count / threshold × 64 (G: dilated, for clouds)
export const SCAN_SKY_GLSL = /* glsl */ `
uniform vec4 uSkyCenter;
uniform vec4 uSkyParams;
uniform vec4 uSkyGrid;
uniform vec4 uSkyGridSize;
uniform highp sampler2DArray uSkyDensity;

float sky_density(vec3 p) {
    if (uSkyGridSize.w < 0.5) return 0.0;
    vec3 g = (p - uSkyGrid.xyz) / uSkyGrid.w;
    if (any(lessThan(g, vec3(0.0))) || any(greaterThanEqual(g, uSkyGridSize.xyz))) return 0.0;
    return texelFetch(uSkyDensity, ivec3(int(g.x), int(g.z), int(g.y)), 0).r * (255.0 / 64.0);
}

void modifySplatCenter(inout vec3 center) {}
void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {}

void modifySplatColor(vec3 center, inout vec4 color) {
    if (uSkyCenter.w < 0.5) return;
    vec3 d = center - uSkyCenter.xyz;
    float r = length(d);
    if (r < uSkyParams.y || r < 1e-5) return;
    float elev = smoothstep(uSkyParams.x, uSkyParams.x + 0.06, d.y / r);
    float far = smoothstep(uSkyParams.y, uSkyParams.y * 1.2, r);
    float sparse = 1.0 - smoothstep(0.6, 1.4, sky_density(center));
    vec3 c = clamp(color.rgb, 0.0, 1.0);
    float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
    float foliage = smoothstep(0.02, 0.1, c.g - max(c.r, c.b));
    // sky streaks and haze come in any brightness; only near-black and foliage green are protected
    float skyColor = smoothstep(0.03, 0.14, lum) * (1.0 - foliage);
    float sky = elev * far * sparse * skyColor;
    float hide = smoothstep(0.3, 0.7, sky) * uSkyParams.z;
    if (uSkyParams.w > 0.5) {
        color.rgb = mix(color.rgb, vec3(1.0, 0.1, 0.8), hide * 0.85);
    } else {
        color.a *= 1.0 - hide;
    }
}
`;

export const SCAN_SKY_WGSL = /* wgsl */ `
uniform uSkyCenter: vec4f;
uniform uSkyParams: vec4f;
uniform uSkyGrid: vec4f;
uniform uSkyGridSize: vec4f;
var uSkyDensity: texture_2d_array<f32>;

fn sky_density(p: vec3f) -> f32 {
    if (uniform.uSkyGridSize.w < 0.5) { return 0.0; }
    let g = (p - uniform.uSkyGrid.xyz) / uniform.uSkyGrid.w;
    if (any(g < vec3f(0.0)) || any(g >= uniform.uSkyGridSize.xyz)) { return 0.0; }
    return textureLoad(uSkyDensity, vec2i(i32(g.x), i32(g.z)), i32(g.y), 0).r * (255.0 / 64.0);
}

fn modifySplatCenter(center: ptr<function, vec3f>) {}
fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    if (uniform.uSkyCenter.w < 0.5) { return; }
    let d = center - uniform.uSkyCenter.xyz;
    let r = length(d);
    if (r < uniform.uSkyParams.y || r < 1e-5) { return; }
    let elev = smoothstep(uniform.uSkyParams.x, uniform.uSkyParams.x + 0.06, d.y / r);
    let far = smoothstep(uniform.uSkyParams.y, uniform.uSkyParams.y * 1.2, r);
    let sparse = 1.0 - smoothstep(0.6, 1.4, sky_density(center));
    let c = clamp((*color).rgb, vec3f(0.0), vec3f(1.0));
    let lum = dot(c, vec3f(0.2126, 0.7152, 0.0722));
    let foliage = smoothstep(0.02, 0.1, c.g - max(c.r, c.b));
    let skyColor = smoothstep(0.03, 0.14, lum) * (1.0 - foliage);
    let sky = elev * far * sparse * skyColor;
    let hide = smoothstep(0.3, 0.7, sky) * uniform.uSkyParams.z;
    if (uniform.uSkyParams.w > 0.5) {
        (*color) = vec4f(mix((*color).rgb, vec3f(1.0, 0.1, 0.8), hide * 0.85), (*color).a);
    } else {
        (*color).a = (*color).a * (1.0 - hide);
    }
}
`;

// ── runtime ──────────────────────────────────────────────────────────────────────────────

export interface ScanSkyDeps {
    app: pc.AppBase;
    /** The scan's gsplat entities (the main splat). */
    scanEntities: () => pc.Entity[];
    /** The capture point in world space when the scene sets none (the start camera). */
    defaultCenter: () => pc.Vec3;
    toPlayCanvasPosition: (value: unknown) => { x: number; y: number; z: number };
    requestFrame: (reason: string) => void;
}

/** What the clouds need from the scan grid (clearance). */
export interface ScanGridBinding {
    texture: pc.Texture;
    origin: [number, number, number];
    cell: number;
    dims: [number, number, number];
}

type GsplatLike = pc.GSplatComponent & {
    resource?: { centers?: ArrayLike<number> | null; centersVersion?: number } | null;
    setWorkBufferModifier?: (m: { glsl: string; wgsl: string } | null) => void;
    setParameter(name: string, value: unknown): void;
};

/**
 * Owns the scan density grid (built once the main splat has centres), the sky classifier on the
 * scan's copy stage, and what the clouds read for clearance.
 */
export class ScanSkyController {
    private readonly deps: ScanSkyDeps;
    private grid: ScanDensityGrid | null = null;
    private gridTexture: pc.Texture | null = null;
    private gridSource: unknown = null;
    private gridVersion = -1;
    private estimate: SkyShellEstimate | null = null;
    private estimateKey = '';
    private settings: ResolvedScanSky = resolveScanSky(null);
    private preview = false;
    private installed = new WeakSet<object>();
    private installedAny = false;
    private pushedKey = new WeakMap<object, string>();
    private placeholder: pc.Texture | null = null;
    /** The editor asked for the measurements (suggest a layer, the panel's info). */
    private demanded = false;
    private builtAt = 0;
    /** Grid builds so far (diagnostics and tests: a scene without clouds builds none). */
    gridBuilds = 0;

    constructor(deps: ScanSkyDeps) {
        this.deps = deps;
    }

    setSettings(settings: ScanSkySettings | null | undefined): void {
        const next = resolveScanSky(settings);
        const changed = JSON.stringify(next) !== JSON.stringify(this.settings);
        this.settings = next;
        // only a change that can show on screen asks for a frame
        if (changed && (this.installedAny || next.mode === 'replace' || this.preview)) this.deps.requestFrame('scan-sky');
    }

    get resolved(): ResolvedScanSky {
        return this.settings;
    }

    /** Editor: tint the splats that 'replace' would hide (magenta) instead of hiding them. */
    setPreview(on: boolean): void {
        if (on === this.preview) return;
        this.preview = on;
        this.deps.requestFrame('scan-sky');
    }

    /**
     * Build the grid now if the scan is loaded (the editor's "suggest a layer" and the panel's
     * measurements); from then on it is kept up to date like a scene with clouds.
     */
    demand(): void {
        this.demanded = true;
        this.ensureGrid();
    }

    /** The grid for cloud clearance, once built. */
    binding(): ScanGridBinding | null {
        if (!this.grid || !this.gridTexture) return null;
        return { texture: this.gridTexture, origin: this.grid.origin, cell: this.grid.cell, dims: this.grid.dims };
    }

    /** Measured objects / sky shell around the capture point (world units), once the grid exists. */
    shell(): (SkyShellEstimate & { center: [number, number, number] }) | null {
        if (!this.grid) return null;
        const c = this.centerWorld();
        const key = `${c.join(',')}|${this.settings.horizon}`;
        if (key !== this.estimateKey || !this.estimate) {
            this.estimate = estimateSkyShell(this.grid, c, this.settings.horizon);
            this.estimateKey = key;
        }
        return { ...this.estimate, center: c };
    }

    private centerWorld(): [number, number, number] {
        const c = this.settings.center;
        if (c) {
            const p = this.deps.toPlayCanvasPosition(c);
            return [p.x, p.y, p.z];
        }
        const d = this.deps.defaultCenter();
        return [d.x, d.y, d.z];
    }

    /** Build the grid when the scan's centres become available or change; returns true when it changed. */
    private ensureGrid(): boolean {
        const entity = this.deps.scanEntities()[0];
        const gs = entity?.gsplat as GsplatLike | undefined;
        const res = gs?.resource as (GsplatLike['resource'] & { octree?: { nodes?: Array<{ bounds: pc.BoundingBox; lods: Array<{ count: number }> }> } | null }) | undefined;
        const centers = res?.centers;
        const nodes = res?.octree?.nodes;
        if (!entity || ((!centers || centers.length < 30) && !nodes?.length)) return false;
        const fromCenters = !!centers && centers.length >= 30;
        if (this.gridSource === res) {
            // A streamed (octree / LOD) scan's grid comes from its octree, which never changes:
            // streaming swapping chunks (centersVersion churn) never rebuilds it. A plain scan
            // rebuilds when its centres change (splat edits), at most every 2 s.
            if (!fromCenters || this.gridVersion === (res?.centersVersion ?? 0)) return false;
            if (performance.now() - this.builtAt < 2000) return false;
        }
        this.gridSource = res;
        this.gridVersion = res?.centersVersion ?? 0;
        this.builtAt = performance.now();
        const m = entity.getWorldTransform();
        const v = new pc.Vec3();
        let world: Float32Array;
        let weights: Float32Array | undefined;
        let k = 0;
        if (fromCenters) {
            // world-space centres (subsampled to ~400k)
            const n = Math.floor(centers.length / 3);
            const stride = Math.max(1, Math.floor(n / 400_000));
            world = new Float32Array(Math.ceil(n / stride) * 3);
            for (let i = 0; i < n; i += stride) {
                v.set(centers[i * 3], centers[i * 3 + 1], centers[i * 3 + 2]);
                m.transformPoint(v, v);
                world[k++] = v.x;
                world[k++] = v.y;
                world[k++] = v.z;
            }
        } else {
            // LOD (octree) scans: no centres on the root; spread each leaf's finest count over
            // samples inside its bounds (deterministic, a lattice per leaf)
            const leaves = nodes!;
            const per = 27;
            world = new Float32Array(leaves.length * per * 3);
            weights = new Float32Array(leaves.length * per);
            for (const node of leaves) {
                const count = node.lods?.[0]?.count ?? 0;
                if (!count) continue;
                const mn = node.bounds.getMin();
                const mx = node.bounds.getMax();
                for (let a = 0; a < 3; a++) for (let b = 0; b < 3; b++) for (let c = 0; c < 3; c++) {
                    v.set(mn.x + ((a + 0.5) / 3) * (mx.x - mn.x), mn.y + ((b + 0.5) / 3) * (mx.y - mn.y), mn.z + ((c + 0.5) / 3) * (mx.z - mn.z));
                    m.transformPoint(v, v);
                    weights[k / 3] = count / per;
                    world[k++] = v.x;
                    world[k++] = v.y;
                    world[k++] = v.z;
                }
            }
        }
        // grid over the central 98% of the scan (far background beyond it is never "solid")
        const bounds = quantileBounds(world.subarray(0, k), 0.01);
        if (!bounds) return false;
        const grid = buildScanDensityGrid(world.subarray(0, k), bounds, 112, 1, weights);
        if (!grid) return false;
        this.grid = grid;
        this.estimate = null;
        this.gridBuilds++;
        this.gridTexture?.destroy();
        const [gx, gy, gz] = grid.dims;
        this.gridTexture = new pc.Texture(this.deps.app.graphicsDevice, {
            name: 'ScanDensityGrid',
            width: gx,
            height: gz,
            arrayLength: gy,
            format: pc.PIXELFORMAT_RG8,
            mipmaps: false,
            minFilter: pc.FILTER_NEAREST,
            magFilter: pc.FILTER_NEAREST,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: pc.ADDRESS_CLAMP_TO_EDGE,
            levels: [densityLayers(grid)] as unknown as Uint8Array[],
        });
        return true;
    }

    /**
     * Per frame: keep the classifier and its uniforms on the scan in step with the settings.
     * `cloudsPresent`: the scene has cloud layers (they need the grid for clearance). A scene
     * with no clouds, no 'replace' and no preview does no work here at all: no grid, no texture,
     * no modifier, no frames.
     */
    update(cloudsPresent = true): boolean {
        const s = this.settings;
        const active = (s.mode === 'replace' && s.strength > 0) || this.preview;
        if (!cloudsPresent && !active && !this.demanded && !this.installedAny) return false;
        const changedGrid = this.ensureGrid();
        const shell = active ? this.shell() : null;
        const c = this.centerWorld();
        const keep = s.keepRadius !== null ? s.keepRadius : shell?.keepRadius ?? 0;
        const grid = this.grid;
        const tex = this.gridTexture ?? this.placeholderTexture();
        let changed = changedGrid;
        for (const entity of this.deps.scanEntities()) {
            const gs = entity.gsplat as GsplatLike | undefined;
            if (!gs?.setWorkBufferModifier) continue;
            if (!active && !this.installed.has(gs)) continue;
            if (!this.installed.has(gs)) {
                // one modifier per component: composed with any other feature's part (wind)
                setWorkBufferModifierPart(gs, 'scanSky', { glsl: SCAN_SKY_GLSL, wgsl: SCAN_SKY_WGSL });
                this.installed.add(gs);
                this.installedAny = true;
            }
            const key = [active ? 1 : 0, ...c, s.horizon, keep, s.strength, this.preview ? 1 : 0, grid ? grid.origin.join(',') : '-', this.gridVersion, tex.name].join('|');
            if (this.pushedKey.get(gs) === key) continue;
            this.pushedKey.set(gs, key);
            gs.setParameter('uSkyCenter', new Float32Array([c[0], c[1], c[2], active ? 1 : 0]));
            gs.setParameter('uSkyParams', new Float32Array([Math.sin((s.horizon * Math.PI) / 180), keep, this.preview ? 1 : s.strength, this.preview ? 1 : 0]));
            gs.setParameter('uSkyGrid', new Float32Array(grid ? [grid.origin[0], grid.origin[1], grid.origin[2], grid.cell] : [0, 0, 0, 1]));
            gs.setParameter('uSkyGridSize', new Float32Array(grid ? [grid.dims[0], grid.dims[1], grid.dims[2], 1] : [1, 1, 1, 0]));
            gs.setParameter('uSkyDensity', tex);
            changed = true;
        }
        if (changed) this.deps.requestFrame('scan-sky');
        return changed;
    }

    private placeholderTexture(): pc.Texture {
        if (!this.placeholder) {
            this.placeholder = new pc.Texture(this.deps.app.graphicsDevice, {
                name: 'ScanDensityPlaceholder',
                width: 1,
                height: 1,
                arrayLength: 1,
                format: pc.PIXELFORMAT_RG8,
                mipmaps: false,
                levels: [[new Uint8Array([0, 0])]] as unknown as Uint8Array[],
            });
        }
        return this.placeholder;
    }

    destroy(): void {
        this.gridTexture?.destroy();
        this.gridTexture = null;
        this.placeholder?.destroy();
        this.placeholder = null;
    }
}

/** Per-axis quantile bounds of xyz triples. */
export function quantileBounds(xyz: ArrayLike<number>, q: number): { min: [number, number, number]; max: [number, number, number] } | null {
    const n = Math.floor(xyz.length / 3);
    if (!n) return null;
    const axes = [0, 1, 2].map((a) => {
        const v = new Float32Array(n);
        for (let i = 0; i < n; i++) v[i] = xyz[i * 3 + a];
        v.sort();
        return [v[Math.floor((n - 1) * q)], v[Math.ceil((n - 1) * (1 - q))]];
    });
    return { min: [axes[0][0], axes[1][0], axes[2][0]], max: [axes[0][1], axes[1][1], axes[2][1]] };
}
